import axios, { AxiosRequestConfig } from "axios";
import axiosRetry from "axios-retry";

// Types
interface ApiError extends Error {
  status?: number;
  code?: string;
  data?: any;
}

interface RefreshTokenResponse {
  token: string;
  refresh_token: string;
}

// Configuration
const BASE_URL = "https://auth-dev.innate.io/api/v1/";
const MAX_RETRIES = 3;
const TIMEOUT = 15000; // 15 seconds
const RETRY_STATUS_CODES = [408, 429, 500, 502, 503, 504];

const api: any = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  timeout: TIMEOUT,
  withCredentials: true,
});

// Loading state management
let loadingCount = 0;
const loadingHandlers = {
  show: () => {},
  hide: () => {},
};

export const setLoadingHandlers = (handlers: {
  show: () => void;
  hide: () => void;
}) => {
  loadingHandlers.show = handlers.show;
  loadingHandlers.hide = handlers.hide;
};

const showLoading = () => {
  loadingCount++;
  if (loadingCount === 1) {
    loadingHandlers.show();
  }
};

const hideLoading = () => {
  loadingCount--;
  if (loadingCount === 0) {
    loadingHandlers.hide();
  }
};

// Configure retry logic
axiosRetry(api, {
  retries: MAX_RETRIES,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error: any) => {
    return (
      axiosRetry.isNetworkOrIdempotentRequestError(error) ||
      RETRY_STATUS_CODES.includes(error.response?.status || 0)
    );
  },
});

// Security headers
// const securityHeaders = {
//   "X-Content-Type-Options": "nosniff",
//   "X-Frame-Options": "DENY",
//   "Strict-Transport-Security": "max-age=31536000; includeSubDomains",
//   "X-XSS-Protection": "1; mode=block",
// } as const;

// Response interceptor
api.interceptors.response.use(
  (response: any) => {
    if (!response.config.skipLoader) {
      hideLoading();
    }

    const requestStartTime = response.config.metadata?.startTime;
    if (requestStartTime) {
      (response as any).duration =
        new Date().getTime() - requestStartTime.getTime();
    }

    return response;
  },
  async (error: any) => {
    if (!error.config?.skipLoader) {
      hideLoading();
    }

    const originalRequest = error.config as AxiosRequestConfig;

    return Promise.reject(createApiError(error));
  }
);

// Helper functions
const handleAuthFailure = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refresh_token");
  window.location.href = "/login";
};

const createApiError = (error: any): ApiError => {
  const apiError: ApiError = new Error(
    error.response?.data?.message ||
      error.message ||
      "An unexpected error occurred"
  );

  apiError.status = error.response?.status;
  apiError.code = error.code;
  apiError.data = error.response?.data;

  if ((import.meta as any).env?.DEV) {
    console.error("[API Error]", {
      status: apiError.status,
      code: apiError.code,
      message: apiError.message,
      data: apiError.data,
    });
  }

  return apiError;
};

// Error handler
export const handleApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    return createApiError(error);
  }
  return createApiError(new Error("An unexpected error occurred"));
};

export default api;
